import { useEffect, useState } from 'react';
import dayjs from 'dayjs';

interface CountdownResult {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
}

const getExpiredCountdown = (): CountdownResult => ({
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
  isExpired: true,
});

const calculateCountdown = (targetDate: string): CountdownResult => {
  if (!targetDate) {
    return getExpiredCountdown();
  }

  const target = dayjs(targetDate);

  if (!target.isValid()) {
    return getExpiredCountdown();
  }

  const now = dayjs();
  const diff = target.diff(now, 'second');

  if (diff <= 0) {
    return getExpiredCountdown();
  }

  const days = Math.floor(diff / (60 * 60 * 24));
  const hours = Math.floor((diff % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((diff % (60 * 60)) / 60);
  const seconds = diff % 60;

  return {
    days,
    hours,
    minutes,
    seconds,
    isExpired: false,
  };
};

export const useCountdown = (targetDate: string): CountdownResult => {
  const [countdown, setCountdown] = useState<CountdownResult>(() => calculateCountdown(targetDate));

  useEffect(() => {
    const updateCountdown = () => {
      setCountdown(calculateCountdown(targetDate));
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  return countdown;
};

export const formatCountdown = (countdown: CountdownResult): string => {
  if (countdown.isExpired) {
    return '0h 0m 0s';
  }

  // Convert days to hours if there are whole days
  const totalHours = countdown.days * 24 + countdown.hours;
  const parts: string[] = [];

  if (totalHours > 0) {
    parts.push(`${totalHours}h`);
  }
  if (countdown.minutes > 0 || totalHours > 0) {
    parts.push(`${countdown.minutes}m`);
  }
  parts.push(`${countdown.seconds}s`);

  return parts.join(' ');
};
